import { typography, radius } from '@/shared/theme/tokens';
import { REC_IMPACT_META } from '../../liveModeMeta';
import { HUD } from './hudTheme';
import { GlassPanel, PanelHeader } from './GlassPanel';

const MONO = typography.fontFamily.mono;
const VISIBLE = 4;

/**
 * Flight-director action stack — the static, priority-ranked recommendations
 * that still need an operator decision. Top item is the flight directive (full
 * detail); the rest are condensed. Open-loop (accepted) recs read as neutrally
 * "awaiting confirmation". Read-only: a click selects the rec. When degraded the
 * advisory is paused and the stack is empty.
 */
export function ActionStack({ recs = [], paused = false, animate, onSelect }) {
  const shown = recs.slice(0, VISIBLE);
  const overflow = recs.length - shown.length;

  return (
    <GlassPanel accent={HUD.accent} style={{ flex: 1 }}>
      <PanelHeader title="Flight Director" accent={HUD.accent} count={paused ? undefined : recs.length} />
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {paused ? (
          <Empty icon="sensors_off" color={HUD.await} text="ADVISORY PAUSED" sub="Inputs are stale — directives held until the feed recovers." />
        ) : shown.length === 0 ? (
          <Empty icon="check_circle" color={HUD.ok} text="NO DIRECTIVES" sub="Nothing needs an operator decision right now." />
        ) : (
          <>
            {shown.map((rec, i) => (
              <DirectiveRow key={rec.id} rec={rec} lead={i === 0} animate={animate} onSelect={onSelect} />
            ))}
            {overflow > 0 && (
              <div style={{
                marginTop: 'auto', fontFamily: MONO, fontSize: typography.fontSize.xs, color: HUD.inkFaint,
                letterSpacing: typography.letterSpacing.wide, padding: '9px 14px',
                borderTop: `1px solid ${HUD.strokeSoft}`, fontVariantNumeric: 'tabular-nums',
              }}>
                +{overflow} MORE IN QUEUE
              </div>
            )}
          </>
        )}
      </div>
    </GlassPanel>
  );
}

function DirectiveRow({ rec, lead, animate, onSelect }) {
  const impact = REC_IMPACT_META[rec.impact] ?? REC_IMPACT_META.low;
  const numerical = rec.action?.type === 'numerical';
  const clickable = typeof onSelect === 'function';
  const openLoop = rec.status === 'accepted';

  return (
    <div
      onClick={clickable ? () => onSelect(rec) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(rec); } } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Open ${rec.parameterName} recommendation` : undefined}
      style={{
        display: 'flex', flexDirection: 'column', gap: 8, padding: lead ? '13px 14px 14px' : '11px 14px',
        borderBottom: `1px solid ${HUD.strokeSoft}`,
        background: lead ? HUD.accentTint : 'transparent',
        borderLeft: lead ? `2px solid ${HUD.accent}` : '2px solid transparent',
        cursor: clickable ? 'pointer' : 'default',
        animation: animate ? 'hud-fade-in 0.3s ease' : 'none',
      }}
    >
      {/* header: lead marker + name + impact */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {lead && (
          <span style={{
            fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
            letterSpacing: typography.letterSpacing.wider, color: HUD.accent,
            background: HUD.accentTint, border: `1px solid ${HUD.accentEdge}`,
            borderRadius: radius.xs, padding: '1px 6px', flexShrink: 0,
          }}>
            DIRECTIVE
          </span>
        )}
        <span style={{
          flex: 1, minWidth: 0, fontFamily: typography.fontFamily.heading,
          fontSize: lead ? typography.fontSize.lg : typography.fontSize.md, fontWeight: typography.fontWeight.bold,
          color: HUD.ink, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {rec.parameterName}
        </span>
        <Tag color={impact.color} bg={impact.bgDark}>{impact.label}</Tag>
      </div>

      {/* setpoint delta */}
      {numerical ? (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ fontSize: lead ? typography.fontSize['2xl'] : typography.fontSize.lg, color: HUD.inkDim }}>
            {rec.action.currentValue}
          </span>
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: HUD.inkFaint }}>arrow_right_alt</span>
          <span style={{
            fontSize: lead ? typography.fontSize['5xl'] : typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold, color: HUD.accent,
          }}>
            {rec.action.recommendedValue}
          </span>
          {rec.unit && <span style={{ fontSize: typography.fontSize.sm, color: HUD.inkFaint }}>{rec.unit}</span>}
        </div>
      ) : (
        <div style={{ fontSize: typography.fontSize.md, color: HUD.ink, lineHeight: 1.45 }}>
          {rec.action?.instruction}
        </div>
      )}

      {/* expected impact — lead only */}
      {lead && rec.expectedImpact && (
        <div style={{ fontSize: typography.fontSize.sm, color: HUD.inkDim, lineHeight: 1.5 }}>
          {rec.expectedImpact}
        </div>
      )}

      {/* footer telemetry */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        fontFamily: MONO, fontSize: typography.fontSize.xs, color: HUD.inkFaint,
        letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 13, color: HUD.inkFaint }}>schedule</span>
          ETA {rec.timeToImpact}
        </span>
        {openLoop && (
          <span style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
            color: HUD.await, background: HUD.awaitTint, border: `1px solid ${HUD.awaitEdge}`,
            borderRadius: radius.full, padding: '2px 8px', textTransform: 'none',
            letterSpacing: typography.letterSpacing.wide,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 13, color: HUD.await }}>hourglass_top</span>
            Acknowledged — awaiting confirmation
          </span>
        )}
      </div>
    </div>
  );
}

function Tag({ color, bg, children }) {
  return (
    <span style={{
      fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
      letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
      color, background: bg, border: `1px solid ${color}`, borderRadius: radius.xs,
      padding: '1px 6px', flexShrink: 0,
    }}>
      {children}
    </span>
  );
}

function Empty({ icon, color, text, sub }) {
  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 10, textAlign: 'center', padding: 24,
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: 30, color }}>{icon}</span>
      <span style={{ fontFamily: MONO, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wide, color: HUD.ink }}>{text}</span>
      <span style={{ fontSize: typography.fontSize.sm, color: HUD.inkFaint, maxWidth: 220, lineHeight: 1.5 }}>{sub}</span>
    </div>
  );
}
